import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Upload, FileText, Youtube, Link as LinkIcon, Check, X as XIcon, Eye, Trash2, Download } from 'lucide-react';
import Swal from 'sweetalert2';

const ProgramResource = ({ program, onClose, onResourceAdded }) => {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all'); // 'all', 'active', 'inactive'
    const [newResource, setNewResource] = useState({
        type: 'pdf',
        title: '',
        url: '',
        file: null,
        status: 'active'
    });

    useEffect(() => {
        if (program) {
            fetchResources();
        }
    }, [program]);

    const fetchResources = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`https://api.codecircle.co/api/programs/${program.id}/resources`);
            setResources(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching resources:', err);
            setError('Failed to load resources');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewResource({
            ...newResource,
            [name]: value
        });
    };

    const handleFileChange = (e) => {
        setNewResource({
            ...newResource,
            file: e.target.files[0]
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('program_id', program.id);
            formData.append('type', newResource.type);
            formData.append('title', newResource.title);
            formData.append('status', newResource.status);

            if (newResource.type === 'pdf' || newResource.type === 'document') {
                formData.append('file', newResource.file);
            } else {
                formData.append('url', newResource.url);
            }

            await axios.post('https://api.codecircle.co/api/program-resources', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Reset form
            setNewResource({
                type: 'pdf',
                title: '',
                url: '',
                file: null,
                status: 'active'
            });

            // Refresh resources list
            fetchResources();

            // Notify parent component
            if (onResourceAdded) {
                onResourceAdded();
            }

            setError(null);
            Swal.fire('Success!', 'Resource added successfully.', 'success');
        } catch (err) {
            console.error('Error adding resource:', err);
            setError('Failed to add resource.');
            Swal.fire('Error!', 'Failed to add resource.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (resourceId, currentStatus) => {
        try {
            const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
            await axios.patch(`https://api.codecircle.co/api/program-resources/${resourceId}`, {
                status: newStatus
            });
            fetchResources();
            Swal.fire('Success!', `Resource marked as ${newStatus}.`, 'success');
        } catch (err) {
            console.error('Error updating resource status:', err);
            setError('Failed to update resource status.');
            Swal.fire('Error!', 'Failed to update resource status.', 'error');
        }
    };

    const handleDeleteResource = async (resourceId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });
    
        if (result.isConfirmed) {
            setLoading(true);
            try {
                await axios.delete(`https://api.codecircle.co/api/program-resources/${resourceId}`);
                fetchResources();
                Swal.fire('Deleted!', 'Your resource has been deleted.', 'success');
            } catch (err) {
                console.error('Error deleting resource:', err);
                setError('Failed to delete resource.');
                Swal.fire('Error!', 'Failed to delete the resource.', 'error');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDownload = async (resourceId, fileName) => {
        try {
            const response = await axios.get(`https://api.codecircle.co/api/program-resources/${resourceId}/download`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error('Error downloading file:', err);
            Swal.fire('Error!', 'Failed to download the file.', 'error');
        }
    };

    const getResourceIcon = (type) => {
        switch (type) {
            case 'pdf':
                return <FileText className="text-danger" size={18} />;
            case 'document':
                return <FileText className="text-primary" size={18} />;
            case 'video':
                return <Youtube className="text-danger" size={18} />;
            case 'link':
                return <LinkIcon className="text-success" size={18} />;
            default:
                return <FileText size={18} />;
        }
    };

    const filteredResources = resources.filter(resource => 
        filter === 'all' || 
        (filter === 'active' && resource.status === 'active') ||
        (filter === 'inactive' && resource.status === 'inactive')
    );

    return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header bg-light">
                        <h5 className="modal-title">
                            <i className="bi bi-file-earmark-medical me-2"></i>
                            {program.title} - Resources Management
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}

                        <form onSubmit={handleSubmit} className="mb-4">
                            <div className="card border-primary">
                                <div className="card-header" style={{ backgroundColor: '#85d446', color: 'white' }}>
                                    <h6 className="mb-0">
                                        <i className="bi bi-plus-circle me-2"></i>
                                        Add New Resource
                                    </h6>
                                </div>
                                <div className="card-body">
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-4">
                                            <label htmlFor="resourceType" className="form-label">Resource Type</label>
                                            <select
                                                className="form-select"
                                                id="resourceType"
                                                name="type"
                                                value={newResource.type}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="pdf">PDF Document</option>
                                                <option value="document">Word Document</option>
                                                <option value="video">YouTube Video</option>
                                                <option value="link">External Link</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4">
                                            <label htmlFor="resourceTitle" className="form-label">Title</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="resourceTitle"
                                                name="title"
                                                value={newResource.title}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <label htmlFor="resourceStatus" className="form-label">Initial Status</label>
                                            <select
                                                className="form-select"
                                                id="resourceStatus"
                                                name="status"
                                                value={newResource.status}
                                                onChange={handleInputChange}
                                            >
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>
                                    </div>

                                    {(newResource.type === 'pdf' || newResource.type === 'document') ? (
                                        <div className="mb-3">
                                            <label htmlFor="resourceFile" className="form-label">Upload File</label>
                                            <div className="input-group">
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    id="resourceFile"
                                                    accept={newResource.type === 'pdf' ? '.pdf' : '.doc,.docx'}
                                                    onChange={handleFileChange}
                                                    required
                                                />
                                                <span className="input-group-text">
                                                    <Upload size={18} />
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="mb-3">
                                            <label htmlFor="resourceUrl" className="form-label">
                                                {newResource.type === 'video' ? 'YouTube URL' : 'External Link URL'}
                                            </label>
                                            <div className="input-group">
                                                <input
                                                    type="url"
                                                    className="form-control"
                                                    id="resourceUrl"
                                                    name="url"
                                                    value={newResource.url}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder={newResource.type === 'video' ? 'https://youtube.com/...' : 'https://example.com'}
                                                />
                                                <span className="input-group-text">
                                                    <LinkIcon size={18} />
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-end">
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Adding...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-save me-2"></i>
                                                    Add Resource
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">
                                <i className="bi bi-collection me-2"></i>
                                Program Resources
                            </h6>
                            <div className="btn-group btn-group-sm">
                                <button 
                                    className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setFilter('all')}
                                >
                                    All
                                </button>
                                <button 
                                    className={`btn ${filter === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setFilter('active')}
                                >
                                    Active
                                </button>
                                <button 
                                    className={`btn ${filter === 'inactive' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setFilter('inactive')}
                                >
                                    Inactive
                                </button>
                            </div>
                        </div>

                        {loading && (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Loading resources...</p>
                            </div>
                        )}

                        {!loading && filteredResources.length === 0 && (
                            <div className="alert alert-info">
                                <i className="bi bi-info-circle me-2"></i>
                                No resources found matching your criteria.
                            </div>
                        )}

                        {!loading && filteredResources.length > 0 && (
                            <div className="table-responsive">
                                <table className="table table-hover table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th width="15%">Type</th>
                                            <th>Title</th>
                                            <th width="12%">Status</th>
                                            <th width="15%">Added Date</th>
                                            <th width="20%">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredResources.map(resource => (
                                            <tr key={resource.id} className={resource.status === 'inactive' ? 'table-secondary' : ''}>
                                                <td className="align-middle">
                                                    <div className="d-flex align-items-center">
                                                        {getResourceIcon(resource.type)}
                                                        <span className="ms-2">
                                                            {resource.type.charAt(0).toUpperCase() + resource.type.slice(1)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="align-middle">
                                                    {resource.status === 'inactive' ? (
                                                        <span className="text-muted" style={{ textDecoration: 'line-through' }}>
                                                            {resource.title}
                                                        </span>
                                                    ) : (
                                                        <span className="fw-medium">{resource.title}</span>
                                                    )}
                                                    {resource.file_path && (
                                                        <div className="text-muted small mt-1">
                                                            {resource.file_path.split('/').pop()}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="align-middle">
                                                    <span
                                                        className={`badge rounded-pill ${resource.status === 'active' ? 'bg-success' : 'bg-secondary'}`}
                                                    >
                                                        {resource.status === 'active' ? (
                                                            <>
                                                                <Check size={14} className="me-1" /> Active
                                                            </>
                                                        ) : (
                                                            <>
                                                                <XIcon size={14} className="me-1" /> Inactive
                                                            </>
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="align-middle">
                                                    {new Date(resource.created_at).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })}
                                                </td>
                                                <td className="align-middle">
                                                    <div className="btn-group btn-group-sm" role="group">
                                                        <button
                                                            className={`btn ${resource.status === 'active' ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                                                            onClick={() => handleToggleStatus(resource.id, resource.status)}
                                                            title={resource.status === 'active' ? 'Deactivate' : 'Activate'}
                                                        >
                                                            {resource.status === 'active' ? (
                                                                <XIcon size={14} />
                                                            ) : (
                                                                <Check size={14} />
                                                            )}
                                                        </button>
                                                        <a
                                                            href={resource.url || `https://api.codecircle.co/storage/${resource.file_path}`}
                                                            className={`btn btn-outline-primary ${resource.status === 'inactive' ? 'disabled' : ''}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={resource.status === 'inactive' ? 'Resource is inactive' : 'View resource'}
                                                        >
                                                            <Eye size={14} />
                                                        </a>
                                                        {resource.file_path && (
                                                            <button
                                                                onClick={() => handleDownload(resource.id, resource.file_path.split('/').pop())}
                                                                className={`btn btn-outline-info ${resource.status === 'inactive' ? 'disabled' : ''}`}
                                                                title={resource.status === 'inactive' ? 'Resource is inactive' : 'Download file'}
                                                            >
                                                                <Download size={14} />
                                                            </button>
                                                        )}
                                                        <button
                                                            className="btn btn-outline-danger"
                                                            onClick={() => handleDeleteResource(resource.id)}
                                                            title="Delete resource"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <i className="bi bi-x-circle me-2"></i>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProgramResource;